import React, { useState } from "react";
import styled from "styled-components";
import CloseIcon from "@material-ui/icons/Close";
import { useLazyAppFetch } from "../../helpers/app-fetch";

interface NotifyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (email: string) => void;
  examName: string;
}

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
  justify-content: center;
  align-items: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 24px;
  padding: 32px;
  max-width: 544px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  height: 577px;

  @media (max-width: 544px) {
    max-width: 390px;
    width: 100%;
    height: 100%;
    max-height: 717px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #1c1c1f;
  border-radius: 16px;
  width: 48px;
  height: 48px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
  text-align: center;

  @media (max-width: 544px) {
    font-size: 20px;
  }
`;

const ModalDescription = styled.p`
  text-align: center;
  margin-bottom: 24px;
  color: #1c1c1f;
  font-size: 16px;
  line-height: 1.5;
  max-width: 344px;
  width: 100%;
`;

const EmailInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #1c1c1f;
  border-radius: 11px;
  font-size: 16px;
  margin-top: 8px;
  margin-bottom: 25px;

  &::placeholder {
    color: #aaa;
  }
`;

const NotificationButton = styled.button`
  width: 100%;
  padding: 14px;
  background-color: #6b46ff;
  color: white;
  border: none;
  border-radius: 16px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background-color: #5535cc;
  }
`;

const EmailLabel = styled.label`
  align-self: flex-start;
  font-size: 14px;
  color: #666;
`;

const NotificationIcon = styled.div`
  margin-bottom: 16px;
`;

const NotificationImage = styled.img`
  width: 160px;
  height: 160px;
`;

const ErrorMessage = styled.p`
  color: #ff3b30;
  font-size: 14px;
  align-self: flex-start;
  margin-bottom: 8px;
`;

const SuccessMessage = styled.p`
  color: #34c759;
  font-size: 16px;
  text-align: center;
  margin-bottom: 16px;
`;

const NotifyModal: React.FC<NotifyModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  examName
}) => {
  const [email, setEmail] = useState("");
  const [success, setSuccess] = useState(false);
  const { LazyFunc, data: response, loading, error } = useLazyAppFetch();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("emailhere", email);
    try {
      const res = await LazyFunc(
        "/api/auth/notifyMe",
        { method: "POST" },
        {
          Email: email,
          Exam: examName
        }
      );

      if (response) {
        setSuccess(true);
        if (onSubmit) {
          onSubmit(email);
        }
        // Reset the form after a short delay to show the success message
        setTimeout(() => {
          setEmail("");
          setSuccess(false);
          onClose();
        }, 200000);
      }
    } catch (err) {
      console.error("Error submitting notification request:", err);
    }
  };

  return (
    <ModalOverlay isOpen={isOpen}>
      <ModalContent>
        <CloseButton onClick={onClose}>
          <CloseIcon style={{ fontSize: "24px" }} />
        </CloseButton>
        <NotificationIcon>
          <NotificationImage src="/assets/home/notify.png" alt="Notification" />
        </NotificationIcon>
        <ModalTitle>Notify Me</ModalTitle>
        <ModalDescription>
          Leave your email and we will let you know when this preparation kit is
          available
        </ModalDescription>
        {success && (
          <SuccessMessage>
            Thanks! You will be notified when this is available.
          </SuccessMessage>
        )}
        <form onSubmit={handleSubmit} style={{ width: "344px" }}>
          <EmailLabel>Email</EmailLabel>
          <EmailInput
            type="email"
            placeholder="example@yourmail.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {!success && (
            <NotificationButton type="submit" disabled={loading || success}>
              {loading ? "Submitting..." : "Get Notification"}
            </NotificationButton>
          )}
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default NotifyModal;
